/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {List, makeStyles} from '@material-ui/core';
import {mapToolStatus} from '@ion/core/src/entities/Asset';
import {
  AssetState,
  CVSAssetType,
  TagReportType,
  ToolAssetType,
  ToolStatus
} from '@ion/core/src/types/entities';
import AssetStatusItem from '@ion/dls/src/components/sidepanel/AssetStatusItem';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import Address from '@ion/dls/src/maps/components/Address';
import styles from './sharedStyles';

interface ToolCardListContentProps {
  asset: ToolAssetType;
  onContainsClick?: (asset: CVSAssetType) => void;
  toolPanelOpen?: {
    assetId?: string | null;
    open?: boolean;
  };
  toolPanelLabels: {
    statusTitle: string;
    statusType: string;
    statusLocation: string;
  };
  disconnectedToolTagReport?: {
    tagReport: TagReportType;
  };
};

// [DLS PACKAGE TODO]: The below styles can safely be ignored as they are implemented
// as a function (ignored due to limitations of material-ui-verifier AST back referencing)
/* eslint-disable ion-eslint-rules/material-ui-verifier */
const useStyles = makeStyles(styles());

/**
 * Defines the content that would be passed to the asset card's list item to be
 * displayed on the sidepanel when viewing a tool
 *
 * ```typescript
 * import React from 'react';
 * import ToolCardListContent from '@/routes/tracking/PanelList';
 *
 * <ToolCardListContent asset={asset}
 *   toolPanelLabels={toolPanelLabels}
 *   disconnectedToolTagReport={disconnectedToolTagReport} />
 *
 * ```
 */
const ToolCardListContent = ({
  asset,
  toolPanelLabels: {
    statusTitle,
    statusType,
    statusLocation
  },
  disconnectedToolTagReport
}: ToolCardListContentProps) => {
  const classes            = useStyles();
  const tagReport          = asset.tagReport;
  const assetType          = asset.assetType;
  const address            = disconnectedToolTagReport?.tagReport?.address;
  const streetDisconnected = address?.street;
  const cityDisconnected   = address?.city;
  const stateDisconnected  = address?.stateProvinceCd;

  return (
    <List>
      <CardItemRow label={statusTitle}
        isLabelColon
        value={
          <AssetStatusItem assetStatus={
            tagReport ? mapToolStatus(tagReport).status : AssetState.TOWED
          } value={
            tagReport ? mapToolStatus(tagReport).value : ToolStatus.DISCONNECTED
          } />
        } />
      <CardItemRow label={statusType}
        isLabelColon
        value={assetType} />
      <CardItemRow label={statusLocation}
        isLabelColon
        value={!!tagReport?.address?.street || !!streetDisconnected
          ? <Address street={tagReport?.address?.address1 ?? tagReport?.address?.street ?? streetDisconnected}
            city={tagReport?.address?.city || cityDisconnected}
            state={tagReport?.address?.stateProvinceCd || stateDisconnected} />
          : <div className={classes.addressText} style={{padding: '4px'}}>NA</div>} />
    </List>
  );
};

export default ToolCardListContent;
